import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import runes from "@-/com.npmjs.runes2";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import { hasLength } from "@-/com.npmjs.ts-array-length";

/** One character of a word, with the meaning shown beneath it on the drill's back. */
export type WordCharacterBack = {
  /** The grapheme's meaning-card back (`app_grapheme_meaning_card.gloss`); null when absent. */
  back: string | null;
  grapheme: string;
};

export function useGetWordCharacterBacksQueryKey() {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);

  return (word: string) => ["wordCharacterBacks", word, { contentDb }] as const;
}

/**
 * Each character of `word`, in order, with its meaning-card back.
 *
 * Drives both halves of the word drill and so is the reason they agree: the back renders one
 * tile per entry, and the expected answer is those same backs joined by spaces. Reading them
 * from one place means the answer the user is graded against is exactly what the back then
 * shows them.
 *
 * Characters are returned in word order INCLUDING repeats (每每 yields two entries), because the
 * per-character tiles and the answer string are both positional. The DB read itself is deduped.
 */
export const [useWordCharacterBacksQueryOptions, wordCharacterBacksQueryFn] = createUseQueryOptions(
  useGetWordCharacterBacksQueryKey,
  async ({ queryKey: [_, word, { contentDb }] }): Promise<WordCharacterBack[]> => {
    const characters = runes(word);
    if (hasLength(characters, 0)) {
      return [];
    }

    const rows = await contentDb(import.meta.location).query.appGraphemeMeaningCard.findMany({
      columns: { gloss: true, grapheme: true },
      where: { grapheme: { in: uniq(characters) } },
    });
    const glossByGrapheme = new Map(rows.map((row) => [row.grapheme, row.gloss]));

    return characters.map((grapheme) => ({
      back: glossByGrapheme.get(grapheme) ?? null,
      grapheme,
    }));
  },
);
